import React, { useState, useEffect } from 'react';
import { toast } from '../Toast';
import { Rocket, Save, Loader2, Building2, Target, Shield, Users, Banknote, ChevronDown, ChevronRight, Plus, Trash2, X, Brain } from 'lucide-react';
import { crmApi } from '../../services/crmApi';
import { api } from '../../services/apiClient';

const TABS = [
  { id: 'company', label: 'Firma', icon: Building2, color: 'text-blue-600' },
  { id: 'project', label: 'Projekt', icon: Target, color: 'text-fuchsia-600' },
  { id: 'budget', label: 'Budżet', icon: Banknote, color: 'text-emerald-600' },
  { id: 'qualification', label: 'Kwalifikowalność', icon: Shield, color: 'text-amber-600' },
  { id: 'hr', label: 'HR & Zespół', icon: Users, color: 'text-indigo-600' },
] as const;

type TabId = typeof TABS[number]['id'];

const GRANT_PROGRAMS: Record<string, string> = {
  fgsa: 'FG SA (PARP)', rpo_malopolska: 'RPO Małopolska', feng: 'FENG', ferc: 'FERC',
  fers: 'FERS', kpo: 'KPO', interreg: 'Interreg', bon_na_cyfryzacje: 'Bon na cyfryzację',
  kredyt_technologiczny: 'Kredyt technologiczny', go_to_brand: 'Go to Brand',
  sciezka_smart: 'Ścieżka SMART', horyzont_europa: 'Horyzont Europa',
  life: 'LIFE', erasmus: 'Erasmus+', other: 'Inny',
};

const PROJECT_TYPES: Record<string, string> = {
  innovation: 'Innowacje / B+R', digitalization: 'Cyfryzacja / AI',
  internationalization: 'Internacjonalizacja', green_transition: 'Zielona transformacja',
  infrastructure: 'Infrastruktura', training: 'Szkolenia', social_economy: 'Ekonomia społeczna',
  rd_cooperation: 'Współpraca z nauką', ecommerce: 'E-commerce',
  prototype: 'Prototypowanie', other: 'Inne',
};

interface Props {
  application: any;
  onSaved: () => void;
}

export const DevGrantDetailSection: React.FC<Props> = ({ application, onSaved }) => {
  const app = application;
  const [activeTab, setActiveTab] = useState<TabId>('company');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [fields, setFields] = useState<Record<string, any>>({});
  const [analyzing, setAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);

  useEffect(() => {
    if (!app) return;
    const f: Record<string, any> = {};
    // Extract all dev_grant_* fields from application
    Object.keys(app).forEach(k => {
      if (k.startsWith('dev_grant_')) f[k] = app[k];
    });
    setFields(f);
    setDirty(false);
  }, [app?.id]);

  const set = (key: string, value: any) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await crmApi.updateApplication(app.id, fields as any);
      setDirty(false);
      onSaved();
    } catch (err) {
      console.error('DevGrant save error:', err);
      toast.error('❌ Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const runAiAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data: res } = await api.post(`/dotacje/auto-analyze/${app.id}`);
      setAiAnalysis(res.analysis);
    } catch { toast.error('Błąd analizy AI'); }
    setAnalyzing(false);
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fuchsia-500 focus:border-fuchsia-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
  const Val = ({ label, value, suffix }: { label: string; value: any; suffix?: string }) => (
    <div>
      <span className="text-xs text-gray-500">{label}</span>
      <p className="text-sm font-medium text-gray-800">{value ?? '—'}{suffix && value ? ` ${suffix}` : ''}</p>
    </div>
  );

  // JSONB helpers
  const keyPeople = Array.isArray(fields.dev_grant_key_people) ? fields.dev_grant_key_people : [];
  const purchases = Array.isArray(fields.dev_grant_planned_purchases) ? fields.dev_grant_planned_purchases : [];

  const tabFilled = (tab: TabId) => {
    const check = (keys: string[]) => keys.some(k => fields[k]);
    switch(tab) {
      case 'company': return check(['dev_grant_company_name', 'dev_grant_company_nip']);
      case 'project': return check(['dev_grant_project_title', 'dev_grant_project_type']);
      case 'budget': return check(['dev_grant_total_cost', 'dev_grant_grant_program']);
      case 'qualification': return check(['dev_grant_is_in_difficulty', 'dev_grant_zus_debt_free']);
      case 'hr': return check(['dev_grant_hr_uop_count']) || keyPeople.length > 0;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Rocket size={22} className="text-fuchsia-600" />
          Dotacje na Rozwój — dane projektu
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={runAiAnalysis} disabled={analyzing}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50">
            {analyzing ? <Loader2 size={16} className="animate-spin" /> : <Brain size={16} />}
            {analyzing ? 'Analizuję...' : 'Analiza AI'}
          </button>
          {dirty && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-fuchsia-600 text-white rounded-lg text-sm font-medium hover:bg-fuchsia-700 disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Zapisz
            </button>
          )}
        </div>
      </div>

      {/* AI Analysis results */}
      {aiAnalysis && (
        <div className="mb-4 p-4 bg-violet-50 border border-violet-200 rounded-lg text-sm">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-violet-800 flex items-center gap-2">
              <Brain size={16} /> Wynik analizy AI
            </h4>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                aiAnalysis.qualified ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
              }`}>
                {aiAnalysis.qualified ? 'Kwalifikuje się' : 'Nie kwalifikuje się'}
              </span>
              {aiAnalysis.score && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  aiAnalysis.score >= 70 ? 'bg-emerald-100 text-emerald-700' : aiAnalysis.score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }`}>
                  {aiAnalysis.score}/100
                </span>
              )}
            </div>
          </div>
          {aiAnalysis.recommendation && (
            <p className="text-violet-700 mb-2">{aiAnalysis.recommendation}</p>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {aiAnalysis.strengths?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-emerald-600 mb-1">Mocne strony:</p>
                <ul className="text-xs text-gray-700 space-y-0.5">
                  {aiAnalysis.strengths.map((s: string, i: number) => <li key={i}>+ {s}</li>)}
                </ul>
              </div>
            )}
            {aiAnalysis.weaknesses?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-600 mb-1">Słabe strony:</p>
                <ul className="text-xs text-gray-700 space-y-0.5">
                  {aiAnalysis.weaknesses.map((w: string, i: number) => <li key={i}>- {w}</li>)}
                </ul>
              </div>
            )}
          </div>
          {aiAnalysis.matching_programs?.length > 0 && (
            <p className="text-xs text-violet-600 mt-2">
              Pasujące programy: {aiAnalysis.matching_programs.join(', ')}
            </p>
          )}
          <button onClick={() => setAiAnalysis(null)} className="mt-2 text-xs text-gray-400 hover:text-gray-600">
            Zamknij
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const filled = tabFilled(tab.id);
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-fuchsia-600 text-fuchsia-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon size={14} className={activeTab === tab.id ? tab.color : ''} />
              {tab.label}
              {filled && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
            </button>
          );
        })}
      </div>

      {/* Company tab */}
      {activeTab === 'company' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className={labelCls}>Nazwa firmy</label>
            <input value={fields.dev_grant_company_name || ''} onChange={e => set('dev_grant_company_name', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>NIP</label>
            <input value={fields.dev_grant_company_nip || ''} onChange={e => set('dev_grant_company_nip', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>REGON</label>
            <input value={fields.dev_grant_company_regon || ''} onChange={e => set('dev_grant_company_regon', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>KRS</label>
            <input value={fields.dev_grant_company_krs || ''} onChange={e => set('dev_grant_company_krs', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Forma prawna</label>
            <input value={fields.dev_grant_legal_form || ''} onChange={e => set('dev_grant_legal_form', e.target.value)} className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Adres</label>
            <input value={fields.dev_grant_company_address || ''} onChange={e => set('dev_grant_company_address', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Wielkość firmy</label>
            <input value={fields.dev_grant_company_size || ''} onChange={e => set('dev_grant_company_size', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Województwo</label>
            <input value={fields.dev_grant_wojewodztwo || ''} onChange={e => set('dev_grant_wojewodztwo', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Pracownicy</label>
            <input type="number" value={fields.dev_grant_employees || ''} onChange={e => set('dev_grant_employees', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Przychód roczny (PLN)</label>
            <input type="number" value={fields.dev_grant_revenue || ''} onChange={e => set('dev_grant_revenue', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
          </div>
        </div>
      )}

      {/* Project tab */}
      {activeTab === 'project' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Typ projektu</label>
              <input value={PROJECT_TYPES[fields.dev_grant_project_type] || fields.dev_grant_project_type || ''} readOnly className={inputCls + ' bg-gray-50'} />
            </div>
            <div>
              <label className={labelCls}>Region realizacji</label>
              <input value={fields.dev_grant_project_region || ''} onChange={e => set('dev_grant_project_region', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Tytuł projektu</label>
            <input value={fields.dev_grant_project_title || ''} onChange={e => set('dev_grant_project_title', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Opis projektu</label>
            <textarea value={fields.dev_grant_project_description || ''} onChange={e => set('dev_grant_project_description', e.target.value)}
              className={inputCls + ' min-h-[80px]'} rows={3} />
          </div>
          <div>
            <label className={labelCls}>Cele i rezultaty</label>
            <textarea value={fields.dev_grant_project_goals || ''} onChange={e => set('dev_grant_project_goals', e.target.value)}
              className={inputCls} rows={2} />
          </div>
          <div>
            <label className={labelCls}>Innowacyjność</label>
            <textarea value={fields.dev_grant_project_innovation || ''} onChange={e => set('dev_grant_project_innovation', e.target.value)}
              className={inputCls} rows={2} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Okres realizacji</label>
              <input value={fields.dev_grant_implementation_period || ''} onChange={e => set('dev_grant_implementation_period', e.target.value)} className={inputCls} placeholder="mies." />
            </div>
            <div>
              <label className={labelCls}>Start</label>
              <input type="date" value={fields.dev_grant_impl_start_date?.split('T')[0] || ''} onChange={e => set('dev_grant_impl_start_date', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Koniec</label>
              <input type="date" value={fields.dev_grant_impl_end_date?.split('T')[0] || ''} onChange={e => set('dev_grant_impl_end_date', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>TRL obecny</label>
              <input value={fields.dev_grant_trl_current || ''} onChange={e => set('dev_grant_trl_current', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>TRL docelowy</label>
              <input value={fields.dev_grant_trl_target || ''} onChange={e => set('dev_grant_trl_target', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Partnerzy projektu</label>
            <input value={fields.dev_grant_project_partners || ''} onChange={e => set('dev_grant_project_partners', e.target.value)} className={inputCls} />
          </div>
        </div>
      )}

      {/* Budget tab */}
      {activeTab === 'budget' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className={labelCls}>Koszt całkowity (PLN)</label>
              <input type="number" value={fields.dev_grant_total_cost || ''} onChange={e => set('dev_grant_total_cost', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Dotacja (PLN)</label>
              <input type="number" value={fields.dev_grant_requested_amount || ''} onChange={e => set('dev_grant_requested_amount', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Wkład własny (PLN)</label>
              <input type="number" value={fields.dev_grant_own_contribution || ''} onChange={e => set('dev_grant_own_contribution', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Program</label>
              <input value={GRANT_PROGRAMS[fields.dev_grant_grant_program] || fields.dev_grant_grant_program || ''} readOnly className={inputCls + ' bg-gray-50'} />
            </div>
          </div>

          {fields.dev_grant_total_cost && fields.dev_grant_requested_amount && (
            <div className="bg-fuchsia-50 rounded-lg p-3 text-sm">
              <strong>Intensywność:</strong>{' '}
              <span className="font-bold text-fuchsia-700">
                {Math.round((fields.dev_grant_requested_amount / fields.dev_grant_total_cost) * 100)}%
              </span>
            </div>
          )}

          <div>
            <label className={labelCls}>Źródło wkładu własnego</label>
            <input value={fields.dev_grant_own_contribution_src || ''} onChange={e => set('dev_grant_own_contribution_src', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Koszty kwalifikowane</label>
            <textarea value={fields.dev_grant_eligible_costs || ''} onChange={e => set('dev_grant_eligible_costs', e.target.value)} className={inputCls} rows={2} />
          </div>
          <div>
            <label className={labelCls}>Kategorie kosztów</label>
            <textarea value={fields.dev_grant_cost_categories || ''} onChange={e => set('dev_grant_cost_categories', e.target.value)} className={inputCls} rows={2} />
          </div>

          {/* Planned purchases JSONB */}
          {purchases.length > 0 && (
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-bold text-gray-700 mb-3">Planowane zakupy</h4>
              {purchases.map((cat: any, ci: number) => (
                <div key={ci} className="mb-3">
                  <p className="text-xs font-semibold text-fuchsia-700 mb-1">{cat.category}</p>
                  {cat.items?.map((item: any, ii: number) => (
                    <div key={ii} className="flex gap-2 mb-1">
                      <input value={item.name || ''} onChange={e => {
                        const newP = [...purchases];
                        newP[ci] = { ...newP[ci], items: newP[ci].items.map((it: any, idx: number) => idx === ii ? { ...it, name: e.target.value } : it) };
                        set('dev_grant_planned_purchases', newP);
                      }} className={inputCls + ' flex-1'} placeholder="Pozycja" />
                      <input type="number" value={item.amount || ''} onChange={e => {
                        const newP = [...purchases];
                        newP[ci] = { ...newP[ci], items: newP[ci].items.map((it: any, idx: number) => idx === ii ? { ...it, amount: e.target.value } : it) };
                        set('dev_grant_planned_purchases', newP);
                      }} className={inputCls + ' w-28'} placeholder="PLN" />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Qualification tab */}
      {activeTab === 'qualification' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: 'dev_grant_is_in_difficulty', l: 'Przedsiębiorstwo w trudnościach?' },
              { k: 'dev_grant_zus_debt_free', l: 'Brak zaległości ZUS?' },
              { k: 'dev_grant_tax_debt_free', l: 'Brak zaległości US?' },
              { k: 'dev_grant_krd_clean', l: 'Czyste KRD?' },
              { k: 'dev_grant_deminimis_received', l: 'Pomoc de minimis?' },
              { k: 'dev_grant_previous_eu_grants', l: 'Wcześniejsze dotacje UE?' },
              { k: 'dev_grant_has_partners', l: 'Ma partnerów?' },
              { k: 'dev_grant_has_export', l: 'Doświadczenie eksportowe?' },
              { k: 'dev_grant_has_ip', l: 'Własność intelektualna?' },
            ].map(({ k, l }) => (
              <div key={k} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <span className="text-xs text-gray-700">{l}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                  fields[k] === 'yes' || fields[k] === true ? 'bg-green-100 text-green-700' :
                  fields[k] === 'no' || fields[k] === false ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400'
                }`}>
                  {fields[k] === 'yes' || fields[k] === true ? 'Tak' : fields[k] === 'no' || fields[k] === false ? 'Nie' : '—'}
                </span>
              </div>
            ))}
          </div>

          {fields.dev_grant_deminimis_received === 'yes' && (
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Kwota de minimis (EUR)</label>
                  <input type="number" value={fields.dev_grant_deminimis_amount || ''} onChange={e => set('dev_grant_deminimis_amount', e.target.value ? parseFloat(e.target.value) : null)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Szczegóły</label>
                  <input value={fields.dev_grant_deminimis_details || ''} onChange={e => set('dev_grant_deminimis_details', e.target.value)} className={inputCls} />
                </div>
              </div>
            </div>
          )}

          {fields.dev_grant_previous_eu_grants === 'yes' && (
            <div>
              <label className={labelCls}>Wcześniejsze dotacje UE — szczegóły</label>
              <textarea value={fields.dev_grant_previous_eu_details || ''} onChange={e => set('dev_grant_previous_eu_details', e.target.value)} className={inputCls} rows={2} />
            </div>
          )}

          {fields.dev_grant_has_partners === 'yes' && (
            <div>
              <label className={labelCls}>Partnerzy — szczegóły</label>
              <textarea value={fields.dev_grant_partner_details || ''} onChange={e => set('dev_grant_partner_details', e.target.value)} className={inputCls} rows={2} />
            </div>
          )}

          {(fields.dev_grant_has_export === 'yes') && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Kraje eksportu</label>
                <input value={fields.dev_grant_export_countries || ''} onChange={e => set('dev_grant_export_countries', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Rynki docelowe</label>
                <input value={fields.dev_grant_target_markets || ''} onChange={e => set('dev_grant_target_markets', e.target.value)} className={inputCls} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* HR tab */}
      {activeTab === 'hr' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className={labelCls}>UoP</label>
              <input type="number" value={fields.dev_grant_hr_uop_count || ''} onChange={e => set('dev_grant_hr_uop_count', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Zlecenie</label>
              <input type="number" value={fields.dev_grant_hr_zlecenie_count || ''} onChange={e => set('dev_grant_hr_zlecenie_count', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>B2B</label>
              <input type="number" value={fields.dev_grant_hr_b2b_count || ''} onChange={e => set('dev_grant_hr_b2b_count', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>% wyższe wykształcenie</label>
              <input type="number" value={fields.dev_grant_hr_higher_edu_pct || ''} onChange={e => set('dev_grant_hr_higher_edu_pct', e.target.value ? parseFloat(e.target.value) : null)} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Szkolenia realizowane</label>
            <textarea value={fields.dev_grant_hr_training_desc || ''} onChange={e => set('dev_grant_hr_training_desc', e.target.value)} className={inputCls} rows={2} />
          </div>

          {/* Key people JSONB */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-indigo-50 px-4 py-2 flex items-center justify-between">
              <h4 className="text-sm font-bold text-indigo-800">Kluczowe osoby ({keyPeople.length})</h4>
              <button onClick={() => set('dev_grant_key_people', [...keyPeople, { name: '', position: '', yearsExp: '', qualifications: '', roleInProject: '' }])}
                className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 flex items-center gap-1">
                <Plus size={12} /> Dodaj
              </button>
            </div>
            {keyPeople.map((person: any, i: number) => (
              <div key={i} className="p-3 border-t flex gap-2 items-start">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <input value={person.name || ''} placeholder="Imię i nazwisko" onChange={e => {
                    const n = [...keyPeople]; n[i] = { ...n[i], name: e.target.value }; set('dev_grant_key_people', n);
                  }} className={inputCls} />
                  <input value={person.position || ''} placeholder="Stanowisko" onChange={e => {
                    const n = [...keyPeople]; n[i] = { ...n[i], position: e.target.value }; set('dev_grant_key_people', n);
                  }} className={inputCls} />
                  <input value={person.roleInProject || ''} placeholder="Rola w projekcie" onChange={e => {
                    const n = [...keyPeople]; n[i] = { ...n[i], roleInProject: e.target.value }; set('dev_grant_key_people', n);
                  }} className={inputCls + ' col-span-2'} />
                </div>
                <button onClick={() => { const n = keyPeople.filter((_: any, idx: number) => idx !== i); set('dev_grant_key_people', n); }}
                  className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
            ))}
            {keyPeople.length === 0 && <p className="text-center text-gray-400 text-xs py-4 italic">Brak kluczowych osób</p>}
          </div>
        </div>
      )}

      {/* Save button */}
      {dirty && (
        <div className="mt-6 pt-4 border-t flex justify-end">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-fuchsia-600 text-white rounded-lg font-medium hover:bg-fuchsia-700 disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Zapisz zmiany
          </button>
        </div>
      )}
    </div>
  );
};
